import type * as React from "react";
import { useCallback, useRef, useState } from "react";

import { useRegisterWallet } from "@/hooks/useClientPositions";
import type { PositionVerificationChallenge } from "@/services/clientPrograms";
import { OrgApiError } from "@/services/orgApiFetch";
import { MICRO_TRANSFER_PROOF_MAX_AGE_MS } from "@/verification";

import { positionSubmitErrorCopy } from "../errorCopy";
import { buildRegisterWalletInput } from "../positionSourcePayload";
import type { SubmitSlice } from "../steps";
import type { VerifiedSource } from "../types";

export interface BoundPositionChallenge extends PositionVerificationChallenge {
    contextKey: string;
}
interface PositionSubmissionOptions {
    verifiedSource: VerifiedSource | null;
    positionChallenge: BoundPositionChallenge | null;
    contextKey: string;
    destination: string;
    submissionKey: string;
    manualReviewDescription: string;
    issuerName: string | null;
}
interface UsePositionSubmissionResult {
    pendingReview: { uuid: string; address: string; description: string; issuerName: string } | null | undefined;
    resetSubmission: () => void;
    handleSubmit: () => void;
    claimRequired: boolean;
    handleConfirmClaim: () => void;
    submitError: string | null;
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>;
    verificationExpired: boolean;
    resetRecovery: () => void;
    recoveryReason: "expired" | "submit-failed" | null;
    registeredPositionCount: number | null;
    submitted: boolean;
    isSubmitting: boolean;
}

export function usePositionSubmission({
    verifiedSource,
    positionChallenge,
    contextKey,
    destination,
    submissionKey,
    manualReviewDescription,
    issuerName
}: PositionSubmissionOptions): UsePositionSubmissionResult {
    const createPosition = useRegisterWallet();
    // Submission uses the captured source/proof; changing the connected signer
    // must not discard its result. Only a source or client-context edit does.
    const latestSubmissionKey = useRef(submissionKey);
    latestSubmissionKey.current = submissionKey;
    const [pendingReview, setPendingReview] = useState<SubmitSlice["pendingReview"]>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [recoveryReason, setRecoveryReason] = useState<"expired" | "submit-failed" | null>(null);
    const verificationExpired = recoveryReason !== null;
    // Stable identities: callers reset these from effects keyed on context changes.
    const resetRecovery = useCallback((): void => {
        setRecoveryReason(null);
    }, []);
    const [claimRequested, setClaimRequested] = useState(false);
    const claimRequired =
        claimRequested ||
        (positionChallenge?.contextKey === contextKey && positionChallenge.requiresRetailAccountClaim === true);
    const [registeredPositionCount, setRegisteredPositionCount] = useState<number | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const resetSubmission = useCallback((): void => {
        setPendingReview(null);
        setSubmitted(false);
        setRegisteredPositionCount(null);
        setClaimRequested(false);
    }, []);
    // Save the wallet and its default for new positions after the final confirmation.
    function submitPosition(confirmClaim = false): void {
        if (claimRequired && !confirmClaim) return;
        if (createPosition.isPending || submitted || verificationExpired) return;
        if (!verifiedSource) {
            setSubmitError("Verify wallet ownership first.");
            return;
        }

        /* The backend rejects micro-transfer proofs older than 15 minutes
         * (MICRO_TRANSFER_MAX_AGE_SECONDS). The proof is captured at the
         * Verify step but redeemed here — a user who dwelled on the
         * destination/review steps would otherwise burn a doomed request
         * and get a generic rejection after having already paid for the
         * transfer. Fail fast with no error line: the action itself reads
         * Verification Expired, then Verify Again, which re-runs the
         * ceremony right here. The
         * expired verification stays in place so the review keeps its source
         * and destination until the fresh proof replaces it. */
        const proof = verifiedSource.proof;
        if (
            proof &&
            (positionChallenge?.contextKey !== contextKey || Date.parse(positionChallenge.expiresAt) <= Date.now())
        ) {
            setRecoveryReason("expired");
            setSubmitError(null);
            return;
        }
        if (
            proof?.method === "microTransfer" &&
            proof.obtainedAt !== undefined &&
            Date.now() - proof.obtainedAt > MICRO_TRANSFER_PROOF_MAX_AGE_MS
        ) {
            setRecoveryReason("expired");
            setSubmitError(null);
            return;
        }

        setSubmitError(null);
        const input = buildRegisterWalletInput({
            source: verifiedSource,
            destination,
            manualReviewDescription
        });

        if (confirmClaim) input.claimRetailAccount = true;
        const submittedKey = submissionKey;
        void (async (): Promise<void> => {
            try {
                const created = await createPosition.mutateAsync(input);
                if (latestSubmissionKey.current !== submittedKey) return;
                if (input.verificationMode === "direct-solomon") {
                    setPendingReview({
                        uuid: created.uuid,
                        address: input.walletAddress,
                        description: input.manualReviewDescription ?? "",
                        issuerName: issuerName?.trim() || "Solomon"
                    });
                }
                setRegisteredPositionCount(created.positionCount);
                setSubmitted(true);
            } catch (error) {
                if (latestSubmissionKey.current !== submittedKey) return;
                if (
                    error instanceof OrgApiError &&
                    (error.code === "RETAIL_ACCOUNT_CLAIM_REQUIRED" ||
                        error.code === "RETAIL_ACCOUNT_CLAIM_REVERIFY_REQUIRED")
                ) {
                    setClaimRequested(true);
                    setSubmitError(null);
                    if (error.code === "RETAIL_ACCOUNT_CLAIM_REVERIFY_REQUIRED") setRecoveryReason("submit-failed");
                    return;
                }
                if (proof) {
                    // The server may have consumed the proof before a later creation failure.
                    setRecoveryReason("submit-failed");
                }
                setSubmitError(positionSubmitErrorCopy(error));
            }
        })();
    }

    return {
        pendingReview,
        resetSubmission,
        handleSubmit: () => submitPosition(false),
        handleConfirmClaim: () => submitPosition(true),
        claimRequired,
        submitError,
        setSubmitError,
        verificationExpired,
        resetRecovery,
        recoveryReason,
        submitted,
        registeredPositionCount,
        isSubmitting: createPosition.isPending
    };
}
